const MEGABYTE = 1024 * 1024;
const GIGABYTE = 1024 * MEGABYTE;

export const MAX_FOLDER_DEPTH = 5;

export const MEDIA_KEYS = Object.freeze({
  TYPE: {
    MEDIA: {
      KEY: 'media',
    },
    SLIDES: {
      MIME: 'image/jpeg',
      KEY: 'slides',
    },
    IMAGES: {
      MIME: 'image/jpeg',
      KEY: 'images',
      MAX_FILE_SIZE: 10 * MEGABYTE,
    },
    VIDEOS: {
      MIME: 'video/webm',
      KEY: 'videos',
      MAX_FILE_SIZE: 2 * GIGABYTE,
    },
    AUDIOS: {
      MIME: 'audio/mp3',
      KEY: 'audios',
      MAX_FILE_SIZE: 10 * MEGABYTE,
    },
    QRCODES: {
      MIME: 'wb/qr',
      KEY: 'qrcodes',
      DRAGTYPE: 'qrcodes'
    },
    CHANNEL: {
      MIME: 'wb/channel',
      KEY: 'channel',
    },
    SERVICES: {
      MIME: 'wb/service',
      KEY: 'services',
    },
  },

  MIME: {
    SLIDES: ['image/jpeg', 'image/png'],
    IMAGES: ['image/jpeg', 'image/png'],
    VIDEOS: [
      'video/mp4',
      'video/webm',
      'video/avi',
      'video/ogg',
      'video/x-flv',
      'video/flv',
      'video/x-msvideo',
      'video/quicktime',
      'video/mpeg',
      'video/mp2t',
      'video/vnd.dlna.mpeg-tts',
    ],
    AUDIOS: [
      'audio/mp3',
      'audio/mpeg',
      'audio/wav',
      'audio/x-wav',
      'audio/x-m4a',
    ],
    QRCODES: [],
    SERVICES: [],
    FOLDERS: ['folders'],
  },
});

export const MEDIAPANE_KEYS = Object.freeze({
  VIEW: {
    GRID: 'grid',
    LIST: 'rows',
    UPLOAD: 'upload',
    CREATE: 'create',
  },

  THUMBNAIL_SIZE: {
    WIDTH: 144,
    HEIGHT: 81,
  },

  // -1 for no max size
  // 7 Fills left pane
  PAGE_SIZE: 15,
});

export const MEDIA_SELECTORS_KEYS = Object.freeze({
  guestRequestNodeCategory: 'guestRequestNodeCategory',
  happenings: 'happenings',
  happeningSlideEditor: 'happeningSlideEditor',
  irdDefaultImageEditor: 'irdDefaultImageEditor',
  irdCategoryImageEditor: 'irdCategoryImageEditor',
  irdNodeItem: 'irdNodeItem',
  sbImageEditor: 'sbImageEditor',
  sbQREditor: 'sbQREditor',
  sbServiceLinkEditor: 'sbServiceLinkEditor',
  sbServiceLinkVideoEditor: 'sbServiceLinkVideoEditor',
  sbMenuLinkEditor: 'sbMenuLinkEditor',
});

export const MEDIA_TYPES = Object.freeze([
  {
    name: 'View All',
    key: MEDIA_KEYS.TYPE.MEDIA.KEY,
    label: 'Media',
    singularLabel: 'Media',
    creatable: false,
    editable: false,
    icon: '',
    accept: '',
  },
  {
    name: 'Videos',
    label: 'Videos',
    singularLabel: 'Video',
    uploadMime: MEDIA_KEYS.MIME.VIDEOS,
    key: MEDIA_KEYS.TYPE.VIDEOS.KEY,
    maxFileSize: 2147483648,
    additionalNotes: [
      {
        heading: 'Recommended Resolution',
        value: '1280 x 720',
      },
      {
        heading: 'Maximum Resolution',
        value: '1920 x 1080',
      },
    ],
    creatable: true,
    editable: false,
    icon: 'play',
    accept:
      'video/mp4, video/x-flv, video/flv, video/avi, video/x-msvideo, video/quicktime, video/mpeg, video/mp2t, video/vnd.dlna.mpeg-tts, .flv, .ts, .tsv',
  },
  {
    name: 'Images',
    label: 'Images',
    singularLabel: 'Image',
    uploadMime: MEDIA_KEYS.MIME.IMAGES,
    key: MEDIA_KEYS.TYPE.IMAGES.KEY,
    maxFileSize: 10485760,
    creatable: true,
    editable: false,
    icon: 'image',
    accept: 'image/jpeg, image/png',
  },
  {
    name: 'Audio',
    label: 'Audio',
    singularLabel: 'Audio',
    uploadMime: MEDIA_KEYS.MIME.AUDIOS,
    key: MEDIA_KEYS.TYPE.AUDIOS.KEY,
    maxFileSize: 10485760,
    creatable: true,
    editable: false,
    icon: 'speaker',
    accept: 'audio/mp3, audio/mpeg, audio/wav, audio/x-wav, audio/x-m4a',
  },
  {
    name: 'QR code',
    label: 'QR codes',
    singularLabel: 'QR code',
    retainCase: true,
    key: MEDIA_KEYS.TYPE.QRCODES.KEY,
    creatable: true,
    editable: true,
    icon: 'qr-code',
    accept: 'qrcodes',
    createFields: [
      {
        type: 'text',
        name: 'title',
        label: 'Name',
        required: true,
      },
      {
        type: 'textarea',
        name: 'destination',
        label: 'Destination URL',
        required: true,
        pattern: '^https?://[^\n]+$',
        placeholder: 'https://examplewebsite.com',
        description: 'Your guests will be sent to this website after scanning the QR code.',
        optionDescription: (
          'Add the desired option to your URL. Make sure you are using the correct variable names for your website.\n' +
          '(ex: https://examplewebsite.com/example?[Variable Name]=[variable value])'
        ),
        options: [
          { label: '+ Room #', value: '[Room#]' },
        ]
      },
    ],
    createActions: false
  },
  {
    name: 'Slides',
    label: 'Slides',
    singularLabel: 'Slide',
    uploadMime: MEDIA_KEYS.MIME.SLIDES,
    key: MEDIA_KEYS.TYPE.SLIDES.KEY,
    creatable: true,
    editable: true,
    icon: 'image',
    accept: 'slide',
  },
  {
    name: 'Service',
    label: 'Services',
    singularLabel: 'Service',
    uploadMime: MEDIA_KEYS.MIME.SERVICES,
    key: MEDIA_KEYS.TYPE.SERVICES.KEY,
    creatable: false,
    editable: true,
    icon: 'image',
    accept: 'image/jpeg, image/png',
  },
]);

export const RECOMMENDED_SIZE = Object.freeze({
  STANDARD: {
    WIDTH: 1280,
    HEIGHT: 720,
  },
  CATEGORY: {
    WIDTH: 550,
    HEIGHT: 410,
  },
  ITEM: {
    WIDTH: 173,
    HEIGHT: 148,
  },
});

export const BROWSER_ITEM_TYPES = Object.freeze({
  FOLDER: 'folder',
  MEDIA: 'media',
});

export const ROOT_TAG = '/media_folder';

export const ROOT_TAG_DEFAULT_NAME = 'media';
